import { Card, IconTile } from "@design";
import { useQuery } from "@tanstack/react-query";
import { MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { chatListSessionsOptions } from "@/api";
import { EntityRow } from "@/components/build/EntityRow";
import { Text } from "@/components/Text";
import { useCapabilities } from "@/features/account/useCurrentUser";
import { buildHost } from "@/features/chat/chatHost";
import { formatRelativeTime } from "@/i18n/format";
import { type BuildEntity, examplePrompts } from "./examplePrompts";

const RECENT = 5;

/**
 * The empty build chat: example prompts this deployment supports, and the latest builds to continue. A chat
 * opened to edit an entity (Edit with AI) asks what should change in it instead.
 */
export function BuildEmptyState({
  entity,
  editing,
  onSend,
}: Readonly<{ entity: BuildEntity | null; editing?: string; onSend: (text: string) => void }>) {
  const { t } = useTranslation("buildChat");
  const navigate = useNavigate();
  const capabilities = useCapabilities();
  const recent = useQuery({
    ...chatListSessionsOptions({ query: { page_size: RECENT, source: "build" } }),
    select: (r) => r.data,
    enabled: editing === undefined,
  });

  if (editing !== undefined) {
    return (
      <div className="py-10 text-center">
        <Text variant="h2" as="h1">
          {t("thread.editTitle", { name: editing })}
        </Text>
        <Text tone="secondary" className="mt-2">
          {t("thread.editSubtitle")}
        </Text>
      </div>
    );
  }
  return (
    <div className="space-y-8 py-10">
      <div className="text-center">
        <Text variant="h2" as="h1">
          {entity ? t("thread.emptyTitleFor", { entity: t(`entityNoun.${entity}`) }) : t("thread.emptyTitle")}
        </Text>
        <Text tone="secondary" className="mt-2">
          {t("thread.emptySubtitle")}
        </Text>
      </div>
      <fieldset className="space-y-2">
        <legend>
          <Text variant="eyebrow" tone="tertiary" as="span">
            {t("thread.examplesLabel")}
          </Text>
        </legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {examplePrompts(capabilities, entity).map((p) => (
            <Card key={p.key} onClick={() => onSend(t(`examples.${p.key}`))}>
              <Text variant="body-sm">{t(`examples.${p.key}`)}</Text>
            </Card>
          ))}
        </div>
      </fieldset>
      {recent.data && recent.data.length > 0 && (
        <section aria-label={t("thread.recentTitle")} className="space-y-2">
          <Text variant="eyebrow" tone="tertiary" as="h2">
            {t("thread.recentTitle")}
          </Text>
          {recent.data.map((row) => (
            <EntityRow
              key={row.uuid}
              icon={
                <IconTile>
                  <MessageCircle size={18} />
                </IconTile>
              }
              name={row.title || t("thread.untitled")}
              description={formatRelativeTime(row.updatedAt)}
              onOpen={() => navigate(buildHost.pathFor(row.uuid))}
            />
          ))}
        </section>
      )}
    </div>
  );
}
